import { TZDate } from "@date-fns/tz";
import { addMinutes, differenceInCalendarDays, differenceInMinutes, startOfDay } from "date-fns";

/**
 * Every date calculation in the dashboard goes through this module.
 *
 * The prototype did not have one, and it cost it both of its headline metrics.
 * `performance.model.js` built a shift boundary as
 *
 *     new Date(`${day}T${shift}:00.000Z`)
 *
 * which reads "08:00" as 08:00 **UTC**. Real punches are stored in true UTC, so
 * a guard clocking in exactly on time at 08:00 Riyadh — 05:00Z — scored 180
 * minutes early, and clocking out at 16:00 local scored 180 minutes of early
 * leave. Every working day. The bug was invisible only because the mock
 * generator emitted its timestamps in UTC to match, so the demo agreed with
 * itself while disagreeing with reality.
 *
 * The second bug was arithmetic on clock strings:
 *
 *     shiftHoursPerDay = shiftEnd - shiftStart
 *
 * which goes negative for a 20:00 → 04:00 night shift and dragged leave hours
 * negative with it. The seed deliberately includes a night-shift guard.
 *
 * Both have the same root cause: a wall-clock time is not an instant, and you
 * cannot subtract one from the other until you have anchored it to a date *and*
 * a zone. Nothing else in the codebase is allowed to call `new Date(...)` on a
 * date string, do `Date` subtraction, or read `getHours()`.
 */

/**
 * Saudi Arabia. No DST — the Kingdom has never observed it — so the offset is a
 * constant +03:00. That is a convenience, not a licence: the arithmetic below
 * is zone-aware regardless, because the org timezone is a setting and a future
 * deployment may not be UTC+3.
 */
export const DEFAULT_TIME_ZONE = "Asia/Riyadh";

/** `HH:mm`, the shape a shift template stores a wall-clock time in. */
export type ClockTime = string;

const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function parseClock(time: ClockTime): {
  hours: number;
  minutes: number;
} {
  const match = CLOCK.exec(time);
  if (!match) throw new Error(`Not an HH:mm time: ${JSON.stringify(time)}`);
  return { hours: Number(match[1]), minutes: Number(match[2]) };
}

/** `YYYY-MM-DD` for the given instant *as seen in* `timeZone`. */
export function toDateKey(instant: Date, timeZone = DEFAULT_TIME_ZONE): string {
  const d = new TZDate(instant, timeZone);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * `YYYY-MM-DDTHH:mm` for a `<input type="datetime-local">` in `timeZone`.
 *
 * The value is a wall clock, not an instant — the API resolves it with
 * `instantAt` the same way attendance corrections do.
 */
export function toLocalDatetimeInput(
  instant: Date,
  timeZone = DEFAULT_TIME_ZONE,
): string {
  const d = new TZDate(instant, timeZone);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${toDateKey(instant, timeZone)}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Coerce an API date (key, Date, or ISO instant) to `YYYY-MM-DD` for date inputs.
 *
 * Profile DATE fields can arrive as ISO instants after JSON serialisation;
 * `<input type="date">` only accepts a key, so anything else renders empty.
 */
export function asDateKey(
  value: string | Date | null | undefined,
  timeZone = DEFAULT_TIME_ZONE,
): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : toDateKey(value, timeZone);
  }
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return toDateKey(parsed, timeZone);
}

/**
 * Anchors a wall-clock time to a calendar day in a zone and returns the real
 * instant. This is the function whose absence caused the timezone bug: given
 * ("2026-08-23", "08:00", "Asia/Riyadh") it returns 2026-08-23T05:00:00Z, not
 * 2026-08-23T08:00:00Z.
 */
export function instantAt(
  dateKey: string,
  time: ClockTime,
  timeZone = DEFAULT_TIME_ZONE,
): Date {
  const { hours, minutes } = parseClock(time);
  const [y, m, d] = dateKey.split("-").map(Number);
  // Constructing a TZDate from parts interprets those parts in `timeZone`,
  // which is precisely the step the prototype skipped.
  return new Date(
    new TZDate(y, m - 1, d, hours, minutes, 0, 0, timeZone).getTime(),
  );
}

/** Midnight in `timeZone` on the day containing `instant`. */
export function startOfLocalDay(
  instant: Date,
  timeZone = DEFAULT_TIME_ZONE,
): Date {
  return new Date(startOfDay(new TZDate(instant, timeZone)).getTime());
}

/**
 * The span a shift actually occupies, as two instants.
 *
 * `crossesMidnight` is not inferred from `end < start` at call time — it is a
 * stored column on the template, so a 00:00 → 00:00 24-hour shift stays
 * expressible and a data-entry mistake cannot silently become a 23-hour shift.
 */
export function shiftSpan(
  dateKey: string,
  start: ClockTime,
  end: ClockTime,
  options: { crossesMidnight: boolean; timeZone?: string },
): { startAt: Date; endAt: Date; minutes: number } {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;
  const startAt = instantAt(dateKey, start, timeZone);
  let endAt = instantAt(dateKey, end, timeZone);

  if (options.crossesMidnight || endAt <= startAt) {
    // Advance by a calendar day in the zone, not by a fixed 1440 minutes: on a
    // DST boundary elsewhere in the world those differ, and the whole point of
    // this module is that the arithmetic survives being moved.
    const nextKey = toDateKey(addMinutes(startAt, 24 * 60), timeZone);
    endAt = instantAt(nextKey, end, timeZone);
  }

  return { startAt, endAt, minutes: differenceInMinutes(endAt, startAt) };
}

/** Positive minutes late, or 0 when on time or early. Never negative. */
export function minutesLate(actual: Date, expected: Date): number {
  return Math.max(0, differenceInMinutes(actual, expected));
}

/** Positive minutes left early, or 0 when they stayed to the end or beyond. */
export function minutesEarly(actual: Date, expected: Date): number {
  return Math.max(0, differenceInMinutes(expected, actual));
}

/**
 * Inclusive list of `YYYY-MM-DD` keys between two instants, in `timeZone`.
 * Used for leave-day enumeration, which the prototype did by string slicing.
 */
export function eachDateKey(
  from: Date,
  to: Date,
  timeZone = DEFAULT_TIME_ZONE,
): string[] {
  if (to < from) return [];
  const keys: string[] = [];
  let cursor = startOfLocalDay(from, timeZone);
  const last = toDateKey(to, timeZone);
  for (let guard = 0; guard < 4000; guard++) {
    const key = toDateKey(cursor, timeZone);
    keys.push(key);
    if (key === last) break;
    cursor = instantAt(
      toDateKey(addMinutes(cursor, 26 * 60), timeZone),
      "00:00",
      timeZone,
    );
  }
  return keys;
}

// ---------------------------------------------------------------------------
// Weeks, for the rota grid
// ---------------------------------------------------------------------------

/**
 * The working week starts on Sunday.
 *
 * Saudi Arabia's weekend is Friday and Saturday, so a rota week runs Sunday
 * through Saturday and the two days off land together at the end of the grid
 * rather than being split across two columns at either edge. `date-fns` defaults
 * to Sunday for `weekStartsOn` anyway, but the value is stated here because it is
 * a business rule about this company's week, not a library default to inherit
 * silently — a deployment in a Monday-start country changes this one constant.
 */
export const WEEK_START_DAY = 0;

/** Days per week, named so the loops below do not read as magic. */
export const DAYS_PER_WEEK = 7;

/** Midnight on the Sunday of the week containing `instant`, in `timeZone`. */
export function startOfLocalWeek(instant: Date, timeZone = DEFAULT_TIME_ZONE): Date {
  const local = new TZDate(instant, timeZone);
  const back = (local.getDay() - WEEK_START_DAY + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  const key = toDateKey(addMinutes(startOfLocalDay(instant, timeZone), -back * 24 * 60), timeZone);
  return instantAt(key, "00:00", timeZone);
}

/**
 * Shifts a `YYYY-MM-DD` key by whole days, in the zone.
 *
 * Stepping through the middle of the day (26 hours, then truncating) rather than
 * adding exactly 1440 minutes: on a DST boundary a "day" is 23 or 25 hours, and
 * adding a fixed 1440 would either repeat or skip a date. The Kingdom has no DST,
 * but this module's whole purpose is arithmetic that survives being moved.
 */
export function addDateKey(dateKey: string, days: number, timeZone = DEFAULT_TIME_ZONE): string {
  let key = dateKey;
  const step = days >= 0 ? 1 : -1;
  for (let i = 0; i < Math.abs(days); i++) {
    const noon = addMinutes(instantAt(key, "12:00", timeZone), step * 24 * 60);
    key = toDateKey(noon, timeZone);
  }
  return key;
}

/**
 * Whole calendar days from `fromKey` to `toKey` in `timeZone`.
 *
 * Same sign as `date-fns` `differenceInCalendarDays`: positive when `to` is
 * after `from`, zero when equal, negative when before. Returns `null` when
 * either key is not a real date — callers must not invent a span from garbage.
 */
export function diffDateKeys(
  fromKey: string,
  toKey: string,
  timeZone = DEFAULT_TIME_ZONE,
): number | null {
  if (!isValidDateKey(fromKey, timeZone) || !isValidDateKey(toKey, timeZone)) return null;
  return differenceInCalendarDays(
    instantAt(toKey, "12:00", timeZone),
    instantAt(fromKey, "12:00", timeZone),
  );
}

/** Today's `YYYY-MM-DD` in the zone. */
export function todayDateKey(now: Date = new Date(), timeZone = DEFAULT_TIME_ZONE): string {
  return toDateKey(now, timeZone);
}

/** The seven `YYYY-MM-DD` keys of the week starting at `weekStartKey`. */
export function weekDateKeys(weekStartKey: string, timeZone = DEFAULT_TIME_ZONE): string[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_, index) =>
    addDateKey(weekStartKey, index, timeZone),
  );
}

/**
 * Whether a string is a real calendar date in `YYYY-MM-DD`.
 *
 * The shape check alone is not enough, because `Date` rolls over out-of-range
 * parts without complaint: `2026-13-45` is well-formed, passes a regex, and
 * constructs as **14 February 2027**. So the parts are round-tripped and the
 * result must come back identical. Without this a hand-edited or truncated
 * `?week=` silently showed a rota six months away — and, worse, would have let a
 * date like that reach a shift row.
 */
export function isValidDateKey(value: string, timeZone = DEFAULT_TIME_ZONE): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const instant = instantAt(value, "12:00", timeZone);
  return !Number.isNaN(instant.getTime()) && toDateKey(instant, timeZone) === value;
}

/**
 * The week a `?week=` parameter names, as a Sunday date key.
 *
 * Anything unparseable falls back to the current week rather than erroring: a
 * stale or hand-edited link should still show the operator a rota.
 */
export function resolveWeekKey(
  raw: string | undefined,
  now: Date = new Date(),
  timeZone = DEFAULT_TIME_ZONE,
): string {
  if (raw && isValidDateKey(raw, timeZone)) {
    return toDateKey(startOfLocalWeek(instantAt(raw, "12:00", timeZone), timeZone), timeZone);
  }
  return toDateKey(startOfLocalWeek(now, timeZone), timeZone);
}

/** Friday or Saturday — drawn differently in the grid, not excluded from it. */
export function isWeekend(dateKey: string, timeZone = DEFAULT_TIME_ZONE): boolean {
  const day = new TZDate(instantAt(dateKey, "12:00", timeZone), timeZone).getDay();
  return day === 5 || day === 6;
}
